import { useCallback, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { useParams } from 'react-router-dom'
import { CellProps, Column } from 'react-table'
import { faLaptopMobile } from '@fortawesome/pro-solid-svg-icons'
import { useMediaQuery } from '@react-hookz/web'

import { GLOBAL_ROUTE_PATHS } from 'Router/Router.constants'

import { LinkButton, PrimaryButton } from 'Components/_theme'
import { TableCard } from 'Components/TableCard/TableCard'

import { PORTAL_BREAKPOINTS } from 'Constants/constants'

import { composePath } from 'Portal/src/utilities/composePath/composePath.util'

import {
  UnreturnedDevicesCardProps,
  UnreturnedDevicesData,
} from './UnreturnedDevicesCard.types'

export const UnreturnedDeviesCard = (props: UnreturnedDevicesCardProps) => {
  const { rentals } = props
  const { organisationId } = useParams<{
    organisationId?: string
  }>()
  const isMobile = useMediaQuery(`(max-width: ${PORTAL_BREAKPOINTS.LG})`)

  const renderDeviceHeader = useCallback(
    () => <FormattedMessage id="Device" />,
    [],
  )

  const renderEmployeeHeader = useCallback(
    () => <FormattedMessage id="Employee" />,
    [],
  )

  const renderActionHeader = useCallback(
    () => <FormattedMessage id="Action" />,
    [],
  )

  const renderDeviceCell = useCallback(
    (cell: React.PropsWithChildren<CellProps<UnreturnedDevicesData>>) => {
      const rental = cell.row.original

      return (
        <div className="d-flex align-items-center">
          <span className="text-line-clamp fw-600">{rental.name}</span>
        </div>
      )
    },
    [],
  )

  const renderEmployeeCell = useCallback(
    (cell: React.PropsWithChildren<CellProps<UnreturnedDevicesData>>) => {
      const rental = cell.row.original

      return (
        <div className="d-flex align-items-center">
          <span className="text-ellipsis">{rental.employee}</span>
        </div>
      )
    },
    [],
  )

  const renderActionCell = useCallback(
    (cell: React.PropsWithChildren<CellProps<UnreturnedDevicesData>>) => {
      const rental = cell.row.original
      const path = composePath({
        params: {
          organisationId,
          rentalId: rental.id,
        },
        path: GLOBAL_ROUTE_PATHS.organisation.rental,
      })

      return (
        <div className="d-flex align-items-center">
          <LinkButton
            appearance="outline"
            aria-label="Action"
            asIcon={faLaptopMobile}
            component={PrimaryButton}
            href={path}
            to={path}
          />
        </div>
      )
    },
    [organisationId],
  )

  const columns = useMemo((): Column<UnreturnedDevicesData>[] => {
    const tableColumns = [
      {
        Cell: renderDeviceCell,
        Header: renderDeviceHeader,
        id: 'device',
      },
      {
        Cell: renderActionCell,
        Header: renderActionHeader,
        id: 'action',
      },
    ]

    if (!isMobile) {
      tableColumns.splice(1, 0, {
        Cell: renderEmployeeCell,
        Header: renderEmployeeHeader,
        id: 'employee',
      })
    }

    return tableColumns
  }, [
    renderDeviceCell,
    renderDeviceHeader,
    renderActionCell,
    renderActionHeader,
    isMobile,
    renderEmployeeCell,
    renderEmployeeHeader,
  ])

  const data = useMemo((): UnreturnedDevicesData[] => rentals, [rentals])

  return (
    <TableCard
      className="mb-4"
      columns={columns}
      data={data}
      heading="UnreturnedDevicesCard_heading"
      icon={faLaptopMobile}
      iconColor="red"
      tableClassName="TableCard--unreturnedDevices"
      total={rentals.length}
    />
  )
}
